import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, vShow as _vShow, withDirectives as _withDirectives, withCtx as _withCtx } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("h2", {
  class: "m-0 text-left text-lg text-textPrimary",
  "data-testid": "station-evidence-title"
}, " Evidência do registro de plantão ", -1)
const _hoisted_2 = ["data-testid"]
const _hoisted_3 = ["data-testid"]
const _hoisted_4 = {
  class: "h-10 w-10 text-neutral50",
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
}
const _hoisted_5 = ["d"]
const _hoisted_6 = /*#__PURE__*/_createElementVNode("p", { class: "m-0 font-bold text-textPrimary" }, "Sem arquivo para exibir", -1)
const _hoisted_7 = { class: "m-0 max-w-sm text-sm text-neutral50" }
const _hoisted_8 = { class: "flex justify-end" }

import { computed, ref, watch } from 'vue'
import { mdiImageOffOutline } from '@mdi/js'

import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import Tabs, { type TabItem } from '@/components/designSystem/Tabs/Tabs.vue'
import CheckVerificationPanel from './CheckVerificationPanel.vue'
import StationActivityEvidencePanel from './StationActivityEvidencePanel.vue'
import StationSheetEvidencePanel from './StationSheetEvidencePanel.vue'
import StationSourceOriginSummary from './StationSourceOriginSummary.vue'
import { EffectiveSourceKind } from '@/constants/ds-attendance.constants'
import type {
  StationEvidenceContext,
  StationEvidenceLeg,
  StationLegKey
} from '@/types/station-evidence.types'


export default /*#__PURE__*/_defineComponent({
  __name: 'StationEvidenceModal',
  props: {
    modelValue: { type: Boolean },
    context: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit }) {

const props = __props;





const activeLeg = ref<StationLegKey>(props.context.initialLeg)
const visitedLegs = ref<StationLegKey[]>([props.context.initialLeg])

const tabItems = computed<TabItem[]>(() =>
  props.context.legs.map((leg) => ({
    label: leg.title,
    value: leg.key,
    testId: `station-evidence-tab-${leg.key}`
  }))
)

const CHECK_KINDS = [EffectiveSourceKind.APP, EffectiveSourceKind.EXTERNAL_APP]

// Sem `refId` não há check para pedir mapa e biometria; sem container não há
// folha nem registro a baixar. Nesses casos a aba cai no estado vazio, que
// explica o motivo, em vez de mostrar um painel que erraria na primeira chamada.
const showsVerification = (leg: StationEvidenceLeg) =>
  Boolean(leg.origin && CHECK_KINDS.includes(leg.origin.kind) && leg.origin.refId)

const showsSheet = (leg: StationEvidenceLeg) =>
  Boolean(
    leg.origin?.kind === EffectiveSourceKind.SHEET && leg.origin.dsAttendanceContainerId != null
  )

const showsActivityRecord = (leg: StationEvidenceLeg) =>
  leg.origin?.kind === EffectiveSourceKind.ACTIVITY_RECORD

const EMPTY_REASONS: Partial<Record<EffectiveSourceKind, string>> = {
  [EffectiveSourceKind.AUTO_PERMANENCE]:
    'Gerada automaticamente pela permanência entre plantões encadeados — não há arquivo a exibir.',
  [EffectiveSourceKind.MANUAL_LEGACY]:
    'Check lançado à mão no painel, antes da aposentadoria do fluxo manual — não há arquivo a exibir.',
  [EffectiveSourceKind.LEGACY]:
    'Horário herdado da base anterior ao ranking de fontes — não há arquivo a exibir.'
}

function emptyReason(leg: StationEvidenceLeg): string {
  const origin = leg.origin
  if (!origin) return 'Esta perna ainda não tem fonte registrada.'
  if (CHECK_KINDS.includes(origin.kind)) {
    return 'O registro não trouxe a referência do check; não é possível abrir mapa e biometria.'
  }
  if (origin.kind === EffectiveSourceKind.SHEET) {
    return 'A linha conciliada não trouxe o identificador da folha.'
  }
  return EMPTY_REASONS[origin.kind] ?? 'Esta fonte não tem arquivo a exibir.'
}

// Visitar é definitivo: a aba entra na lista e nunca sai enquanto o modal viver.
watch(activeLeg, (leg) => {
  if (!visitedLegs.value.includes(leg)) visitedLegs.value.push(leg)
})

// Trocar de plantão com o modal aberto mostraria a evidência do plantão anterior.
watch(
  () => props.context,
  (context) => {
    activeLeg.value = context.initialLeg
    visitedLegs.value = [context.initialLeg]
  }
)

function close() {
  emit('update:modelValue', false)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    "modal-active": _ctx.modelValue,
    "has-header-border": true,
    "has-footer-border": true,
    "max-width": "max-w-6xl",
    "max-height": "90vh",
    "data-testid": "station-evidence-modal",
    onClose: close
  }, {
    header: _withCtx(() => [
      _hoisted_1
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_8, [
        _createVNode(OutlineButton, {
          text: "Fechar",
          width: "100px",
          height: "32px",
          "data-testid": "station-evidence-close-button",
          onClick: close
        })
      ])
    ]),
    default: _withCtx(() => [
      _createVNode(Tabs, {
        modelValue: activeLeg.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeLeg).value = $event)),
        tabs: tabItems.value,
        "data-testid": "station-evidence-tabs"
      }, null, 8, ["modelValue", "tabs"]),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.context.legs, (leg) => {
        return (_openBlock(), _createElementBlock(_Fragment, {
          key: leg.key
        }, [
          (visitedLegs.value.includes(leg.key))
            ? _withDirectives((_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "flex flex-col gap-4 pt-4 text-left",
                "data-testid": `station-evidence-panel-${leg.key}`
              }, [
                (leg.origin)
                  ? (_openBlock(), _createBlock(StationSourceOriginSummary, {
                      key: 0,
                      leg: leg.key,
                      source: leg.origin,
                      at: leg.at,
                      timezone: _ctx.context.timezone
                    }, null, 8, ["leg", "source", "at", "timezone"]))
                  : _createCommentVNode("", true),
                (showsVerification(leg))
                  ? (_openBlock(), _createBlock(CheckVerificationPanel, {
                      key: 1,
                      "check-id": leg.origin?.refId ?? null,
                      events: [leg.key],
                      "testid-prefix": `station-evidence-${leg.key}`,
                      "map-height-px": 200
                    }, null, 8, ["check-id", "events", "testid-prefix"]))
                  : (showsSheet(leg))
                    ? (_openBlock(), _createBlock(StationSheetEvidencePanel, {
                        key: 2,
                        "container-id": leg.origin?.dsAttendanceContainerId ?? null,
                        leg: leg.key
                      }, null, 8, ["container-id", "leg"]))
                    : (showsActivityRecord(leg))
                      ? (_openBlock(), _createBlock(StationActivityEvidencePanel, {
                          key: 3,
                          "container-id": leg.origin?.dsAttendanceContainerId ?? null,
                          attachments: leg.attachments,
                          leg: leg.key
                        }, null, 8, ["container-id", "attachments", "leg"]))
                      : (_openBlock(), _createElementBlock("div", {
                          key: 4,
                          class: "flex min-h-[240px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center",
                          "data-testid": `station-evidence-empty-${leg.key}`
                        }, [
                          (_openBlock(), _createElementBlock("svg", _hoisted_4, [
                            _createElementVNode("path", {
                              d: _unref(mdiImageOffOutline),
                              fill: "currentColor"
                            }, null, 8, _hoisted_5)
                          ])),
                          _hoisted_6,
                          _createElementVNode("p", _hoisted_7, _toDisplayString(emptyReason(leg)), 1)
                        ], 8, _hoisted_3))
              ], 8, _hoisted_2)), [
                [_vShow, activeLeg.value === leg.key]
              ])
            : _createCommentVNode("", true)
        ], 64))
      }), 128))
    ]),
    _: 1
  }, 8, ["modal-active"]))
}
}

})